import { COMMON_TRANSLATION_KEYS } from "constants/commonTranslationsKeys";
import {
  ACTION_CREATE,
  ACTION_DETAILS,
  ACTION_LIST,
  ACTION_UPDATE,
} from "models/role/action";
import {
  SUBJECT_VX2_ONBOARDING,
  SUBJECT_VX2_PAYMENT,
  SUBJECT_VX2_SETTLEMENT,
  SUBJECT_VX2_SETTLEMENT_CONFIGURATION,
  SUBJECT_VX2_WALLET,
} from "models/role/subject";
import { lazyRetry } from "utils/lazyRetry";

import { PAGE_URLS } from "../constants/PageUrls";
import { TRANSLATION_KEYS } from "../constants/TranslationKeys";

import type { Route } from "routes/RouteModel";

const Onboarding = lazyRetry(
  () => import("../pages/Onboarding"),
  "StablecoinsOnboarding"
);

const Wallets = lazyRetry(
  () => import("../pages/Wallets"),
  "StablecoinsWallets"
);

const WalletPage = lazyRetry(
  () => import("../pages/wallet"),
  "StablecoinsWalletPage"
);

const WalletNewPage = lazyRetry(
  () => import("../pages/WalletNew"),
  "StablecoinsWalletNew"
);

const WalletSettlementEditPage = lazyRetry(
  () => import("../pages/wallet/SettlementSettingsEdit"),
  "StablecoinsWalletSettlementEdit"
);

const RunSettlementPage = lazyRetry(
  () => import("../pages/RunSettlement"),
  "StablecoinsRunSettlement"
);

const BeneficiariesPage = lazyRetry(
  () => import("../pages/Beneficiaries"),
  "StablecoinsBeneficiaries"
);

const BeneficiaryNewPage = lazyRetry(
  () => import("../pages/BeneficiaryNew"),
  "StablecoinsBeneficiaryNew"
);

const BeneficiaryEditPage = lazyRetry(
  () => import("../pages/BeneficiaryEdit"),
  "StablecoinsBeneficiaryEdit"
);

const SettlementDestinationsPage = lazyRetry(
  () => import("../pages/SettlementDestinations"),
  "StablecoinsSettlementDestinations"
);

const BankAccountNewPage = lazyRetry(
  () => import("../pages/BankAccountNew"),
  "StablecoinsBankAccountNew"
);

const BankAccountEditPage = lazyRetry(
  () => import("../pages/BankAccountEdit"),
  "StablecoinsBankAccountEdit"
);

const CryptoWalletNewPage = lazyRetry(
  () => import("../pages/CryptoWalletNew"),
  "StablecoinsCryptoWalletNew"
);

const CryptoWalletEditPage = lazyRetry(
  () => import("../pages/CryptoWalletEdit"),
  "StablecoinsCryptoWalletEdit"
);

const PaymentsListPage = lazyRetry(
  () => import("../pages/PaymentsList"),
  "StablecoinsPaymentsList"
);

const PaymentPage = lazyRetry(
  () => import("../pages/PaymentPage"),
  "StablecoinsPaymentPage"
);

const WalletTransferPage = lazyRetry(
  () => import("../pages/WalletTransfer"),
  "StablecoinsWalletTransfer"
);

const WalletTransferDetailsPage = lazyRetry(
  () => import("../pages/WalletTransferDetails"),
  "StablecoinsWalletTransferDetails"
);

const SettlementDetailsPage = lazyRetry(
  () => import("../pages/SettlementDetails"),
  "StablecoinsSettlementDetails"
);

export const stablecoinsRoutes: Route = {
  path: PAGE_URLS.onboarding,
  title: [COMMON_TRANSLATION_KEYS.stablecoins],
  component: Onboarding,
  can: {
    or: [
      { do: ACTION_UPDATE, on: SUBJECT_VX2_ONBOARDING },
      { do: ACTION_DETAILS, on: SUBJECT_VX2_ONBOARDING },
    ],
  },
};

export const stablecoinsWalletsRoutes: Route = {
  path: PAGE_URLS.wallets,
  component: Wallets,
  title: [COMMON_TRANSLATION_KEYS.stablecoins, COMMON_TRANSLATION_KEYS.wallets],
  showChildrenInSidebar: false,
  can: {
    do: ACTION_LIST,
    on: SUBJECT_VX2_WALLET,
  },
  children: [
    {
      path: PAGE_URLS.walletsFiat,
      component: Wallets,
      can: {
        do: ACTION_LIST,
        on: SUBJECT_VX2_WALLET,
      },
    },
    {
      path: PAGE_URLS.walletsCrypto,
      component: Wallets,
      can: {
        do: ACTION_LIST,
        on: SUBJECT_VX2_WALLET,
      },
    },
    {
      path: PAGE_URLS.walletsNew,
      component: WalletNewPage,
      can: {
        do: ACTION_CREATE,
        on: SUBJECT_VX2_WALLET,
      },
    },
    {
      path: PAGE_URLS.walletRunSettlement,
      component: RunSettlementPage,
      can: {
        do: ACTION_UPDATE,
        on: SUBJECT_VX2_SETTLEMENT_CONFIGURATION,
      },
    },
    {
      path: PAGE_URLS.walletSettlementConfigure,
      component: WalletSettlementEditPage,
      can: {
        do: ACTION_UPDATE,
        on: SUBJECT_VX2_SETTLEMENT_CONFIGURATION,
      },
    },
    {
      path: PAGE_URLS.walletSettlementSettings,
      component: WalletPage,
      hasNestedSwitch: true,
      can: {
        do: ACTION_DETAILS,
        on: SUBJECT_VX2_WALLET,
      },
    },
    {
      path: PAGE_URLS.walletDetails,
      component: WalletPage,
      hasNestedSwitch: true,
      can: {
        do: ACTION_DETAILS,
        on: SUBJECT_VX2_WALLET,
      },
    },
  ],
};

export const stablecoinsBeneficiariesRoutes: Route = {
  title: [
    COMMON_TRANSLATION_KEYS.stablecoins,
    COMMON_TRANSLATION_KEYS.beneficiaries,
  ],
  path: PAGE_URLS.beneficiaries,
  component: BeneficiariesPage,
  showChildrenInSidebar: false,
  can: {
    do: ACTION_DETAILS,
    on: SUBJECT_VX2_SETTLEMENT_CONFIGURATION,
  },
  children: [
    {
      path: PAGE_URLS.beneficiaryNew,
      component: BeneficiaryNewPage,
      can: {
        do: ACTION_UPDATE,
        on: SUBJECT_VX2_SETTLEMENT_CONFIGURATION,
      },
    },
    {
      path: PAGE_URLS.beneficiaryEdit,
      component: BeneficiaryEditPage,
      can: {
        do: ACTION_UPDATE,
        on: SUBJECT_VX2_SETTLEMENT_CONFIGURATION,
      },
    },
  ],
};

export const stablecoinsSettlementDestinationsRoutes: Route = {
  title: [
    COMMON_TRANSLATION_KEYS.stablecoins,
    TRANSLATION_KEYS.settlementDestinationsTitle,
  ],
  path: PAGE_URLS.settlementDestinations,
  component: SettlementDestinationsPage,
  showChildrenInSidebar: false,
  can: {
    do: ACTION_DETAILS,
    on: SUBJECT_VX2_SETTLEMENT_CONFIGURATION,
  },
  children: [
    {
      path: PAGE_URLS.bankAccountNew,
      component: BankAccountNewPage,
      can: {
        do: ACTION_UPDATE,
        on: SUBJECT_VX2_SETTLEMENT_CONFIGURATION,
      },
    },
    {
      path: PAGE_URLS.bankAccountEdit,
      component: BankAccountEditPage,
      can: {
        do: ACTION_UPDATE,
        on: SUBJECT_VX2_SETTLEMENT_CONFIGURATION,
      },
    },
    {
      path: PAGE_URLS.cryptoWalletNew,
      component: CryptoWalletNewPage,
      can: {
        do: ACTION_UPDATE,
        on: SUBJECT_VX2_SETTLEMENT_CONFIGURATION,
      },
    },
    {
      path: PAGE_URLS.cryptoWalletEdit,
      component: CryptoWalletEditPage,
      can: {
        do: ACTION_UPDATE,
        on: SUBJECT_VX2_SETTLEMENT_CONFIGURATION,
      },
    },
  ],
};

export const stablecoinsPaymentsRoutes: Route = {
  title: [
    COMMON_TRANSLATION_KEYS.stablecoins,
    COMMON_TRANSLATION_KEYS.payments,
  ],
  path: PAGE_URLS.payments,
  component: PaymentsListPage,
  showChildrenInSidebar: false,
  can: {
    do: ACTION_LIST,
    on: SUBJECT_VX2_PAYMENT,
  },
  children: [
    {
      path: PAGE_URLS.paymentsTransfer,
      component: WalletTransferPage,
      can: {
        do: ACTION_CREATE,
        on: SUBJECT_VX2_WALLET,
      },
    },
    {
      path: PAGE_URLS.paymentsTransferDetails,
      component: WalletTransferDetailsPage,
      can: {
        do: ACTION_DETAILS,
        on: SUBJECT_VX2_WALLET,
      },
    },
    {
      path: PAGE_URLS.paymentsSettlementDetails,
      component: SettlementDetailsPage,
      can: {
        do: ACTION_DETAILS,
        on: SUBJECT_VX2_SETTLEMENT,
      },
    },
    {
      path: PAGE_URLS.paymentDetails,
      component: PaymentPage,
      can: {
        do: ACTION_DETAILS,
        on: SUBJECT_VX2_PAYMENT,
      },
      hasNestedSwitch: true,
    },
  ],
};
